import React, { useRef, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircleFilledIcon } from '@/src/renderer/components/icons'
import { ClockIcon } from './WhenDropdownIcons'
import { escapeBelongsToTaskDetails } from '../../utils/inlineEscape'
import { getNextHalfHour } from './whenDropdownUtils'
// Die Klassen `when-dropdown__…` stehen in WhenDropdown.scss — auch, wenn nur Deadline diesen Teil nutzt.
import './WhenDropdown.scss'

interface TimePointRowProps {
  selectedTime: string
  timeSource: 'period' | 'time' | null
  hasSelection: boolean
  onTimeChange: (time: string) => void
  onApplyTime: (time: string) => void
  onClearTime: () => void
  onConfirm: () => void
  onClose: () => void
  /**
   * Ob die Zeile offen in den **Aufgaben-Details** steht. Dann gehört Escape der Spalte,
   * nicht der Zeile — siehe {@link escapeBelongsToTaskDetails}.
   */
  inline?: boolean
}

export const TimePointRow: React.FC<TimePointRowProps> = ({
  selectedTime,
  timeSource,
  hasSelection,
  onTimeChange,
  onApplyTime,
  onClearTime,
  onConfirm,
  onClose,
  inline = false,
}) => {
  const { t } = useTranslation()
  const timeInputRef = useRef<HTMLInputElement>(null)
  const [timeInputFocused, setTimeInputFocused] = useState(false)
  const nextHalfHourRef = useRef(getNextHalfHour())

  const isActive = timeSource === 'time' && !!selectedTime

  const handleTimeInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9:]/g, '')

    // Auto-format: when user types 2 digits, append ":"
    if (/^\d{2}$/.test(raw) && !selectedTime.includes(':')) {
      const h = parseInt(raw, 10)
      if (h >= 0 && h <= 23) {
        const formatted = `${raw}:00`
        onTimeChange(formatted)
        onApplyTime(formatted)
        requestAnimationFrame(() => {
          timeInputRef.current?.setSelectionRange(3, 5)
        })
        return
      }
    }

    if (/^\d{0,2}:?\d{0,2}$/.test(raw)) {
      onTimeChange(raw)
      if (/^\d{1,2}:\d{2}$/.test(raw)) {
        onApplyTime(raw)
      }
    }
  }, [selectedTime, onTimeChange, onApplyTime])

  const handleTimeKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // Vor dem `stopPropagation`: In den Aufgaben-Details muss Escape hinaus zur Spalte.
    if (escapeBelongsToTaskDetails(e, inline)) return
    e.stopPropagation()
    if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
      return
    }
    if (e.key === 'Enter' && hasSelection) {
      e.preventDefault()
      onConfirm()
      return
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault()
      const input = timeInputRef.current
      if (!input || !selectedTime.includes(':')) return
      if (e.key === 'ArrowLeft') {
        input.setSelectionRange(0, 2)
      } else {
        input.setSelectionRange(3, 5)
      }
      return
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault()
      const input = timeInputRef.current
      const cursorPos = input?.selectionStart ?? 0
      const inHours = cursorPos <= 2
      const isUp = e.key === 'ArrowUp'

      const match = selectedTime.match(/^(\d{1,2}):(\d{2})$/)
      let hours: number
      let minutes: number
      if (match) {
        hours = parseInt(match[1], 10)
        minutes = parseInt(match[2], 10)
      } else {
        hours = (new Date().getHours() + 1) % 24
        minutes = 0
      }

      if (e.shiftKey) {
        if (isUp) {
          minutes = minutes < 30 ? 30 : 0
          if (minutes === 0) hours = (hours + 1) % 24
        } else {
          minutes = minutes > 30 ? 30 : minutes > 0 ? 0 : 30
          if (minutes === 30) hours = ((hours - 1) % 24 + 24) % 24
        }
      } else {
        if (inHours) {
          hours = ((hours + (isUp ? 1 : -1)) % 24 + 24) % 24
        } else {
          const totalMinutes = hours * 60 + minutes + (isUp ? 5 : -5)
          const wrapped = ((totalMinutes % 1440) + 1440) % 1440
          hours = Math.floor(wrapped / 60)
          minutes = wrapped % 60
        }
      }

      const h = String(hours).padStart(2, '0')
      const m = String(minutes).padStart(2, '0')
      const newTime = `${h}:${m}`
      onTimeChange(newTime)
      onApplyTime(newTime)

      requestAnimationFrame(() => {
        if (inHours) {
          input?.setSelectionRange(0, 2)
        } else {
          input?.setSelectionRange(3, 5)
        }
      })
    }
  }, [inline, onClose, selectedTime, hasSelection, onConfirm, onTimeChange, onApplyTime])

  const handleTimeFocus = useCallback(() => {
    setTimeInputFocused(true)
    /*
      **In den Aufgaben-Details füllt der Fokus nichts vor.**

      Im Popover ist die vorgeschlagene halbe Stunde ein Angebot — „Bestätigen"
      steht noch davor. In `inline` wird jede Änderung sofort geschrieben
      (ADR 0026): Wer mit Tab durch die Details läuft, setzte sonst im
      Vorbeigehen eine Uhrzeit an „Wann" UND an die Deadline. Der Vorschlag
      steht dort als Platzhalter, und die Pfeiltasten beginnen bei ihm.
    */
    if (inline) return
    if (!selectedTime) {
      const prefilled = nextHalfHourRef.current
      onTimeChange(prefilled)
      onApplyTime(prefilled)
      requestAnimationFrame(() => {
        timeInputRef.current?.setSelectionRange(0, 2)
      })
    }
  }, [inline, selectedTime, onTimeChange, onApplyTime])

  const handleTimeClear = useCallback(() => {
    onClearTime()
    setTimeInputFocused(false)
  }, [onClearTime])

  return (
    <div
      className={`when-dropdown__time-row${isActive ? ' when-dropdown__time-row--selected' : ''}${timeInputFocused && !isActive ? ' when-dropdown__time-row--focused' : ''}`}
      onClick={() => timeInputRef.current?.focus()}
    >
      <div className="when-dropdown__time-label">
        <ClockIcon size={16} />
        <span>{t('taskCreation.whenTimepoint')}</span>
      </div>
      <div className="when-dropdown__time-right">
        <div className="when-dropdown__time-input-wrapper">
          {isActive && (
            <CheckCircleFilledIcon size={16} className="when-dropdown__time-check" />
          )}
          <input
            ref={timeInputRef}
            type="text"
            inputMode="numeric"
            className="when-dropdown__time-input"
            placeholder={nextHalfHourRef.current}
            maxLength={5}
            value={selectedTime}
            onChange={handleTimeInput}
            onKeyDown={handleTimeKeyDown}
            onFocus={handleTimeFocus}
            onBlur={() => setTimeInputFocused(false)}
          />
          {isActive && (
            <button
              type="button"
              className="when-dropdown__time-clear"
              aria-label={t('taskCreation.whenTimeClear')}
              onClick={handleTimeClear}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.67 0.67L0.67 8.67M0.67 0.67L8.67 8.67" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
